import C from "@/modules/billing/choose_method/ChooseMethod.module.css";

import { WButton } from "@/components/button/WButton.tsx";
import { SiteCurrencySymbol } from "@/components/l10n/Currency.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { currenciesAtom, siteCurrencyInfoAtom } from "@/modules/currency/currencyInfo.ts";
import { displayCurrencyAtom } from "@/modules/currency/displayCurrency.store.ts";
import { ExchangeRateExample } from "@/modules/currency/ExchangeRateExample.tsx";
import { useCurrentCurrency } from "@/modules/currency/useCurrentCurrency.ts";
import { useInAdmin } from "@/modules/main/adminContext.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useAtomValue, useSetAtom } from "jotai/react";
import { useState } from "react";

function CurrencyButton() {
    const currency = useCurrentCurrency();
    const currencies = useAtomValue(currenciesAtom);

    // Only show the button if there's something to actually choose from.
    if (currencies.length < 2) {
        return null;
    }

    return (
        <WModal
            button={
                <WButton color="primary" variant="ghost" size="bar" icon="i-lucide:coins" action={undefined}>
                    <span className="hidden-mobile color-text">{currency.title}</span>
                </WButton>
            }
        >
            <CurrencyModalContent />
        </WModal>
    );
}

export function CurrencyModalContent() {
    //#region Hooks
    const { t } = useLingui();
    const currencies = useAtomValue(currenciesAtom);
    const siteCurrencyInfo = useAtomValue(siteCurrencyInfoAtom);
    const currentCurrency = useCurrentCurrency();

    const setDisplayCurrency = useSetAtom(displayCurrencyAtom);
    const [selectedCode, setSelectedCode] = useState(currentCurrency.code);

    const isAdmin = useInAdmin();

    //#endregion

    function handleSubmit() {
        setDisplayCurrency(selectedCode);
    }

    return (
        <WModalContent
            className="max-w-32rem"
            title={t`Display currency`}
            label={t`Save`}
            modalAction={() => handleSubmit()}
        >
            <p>
                <Trans>
                    Select your display currency to view prices in your preferred currency. Displayed amounts and
                    exchange rates are indicative and for informational purposes only. All transactions are processed in
                    the underlying currency ({siteCurrencyInfo.title}), and final charges may vary based on your payment
                    provider's rates.
                </Trans>
            </p>

            {isAdmin && (
                <NoticeBlock className="mt-3" color="error">
                    In the administrative console, values will be displayed in the selected currency, but{" "}
                    <b>
                        configuration editing fields will always use the base currency (<SiteCurrencySymbol />)
                    </b>
                    .
                </NoticeBlock>
            )}

            <p className="font-size-title pt-3 pb-2">
                <Trans>Select your preferred currency</Trans>
            </p>
            <div className={C.modalList}>
                <div data-disabled={true} className={cn(C.ListItem, C.HeaderItem)} onClick={undefined}>
                    <div className={C.item}>
                        <div className="flex items-center justify-between">
                            <div>
                                <Trans>Display currency</Trans>
                            </div>
                            <div className="w-60">
                                <Trans>Exchange rate</Trans>
                            </div>
                        </div>
                    </div>
                </div>

                {currencies.map((currency) => (
                    <div
                        key={currency.code}
                        // biome-ignore lint/a11y/noNoninteractiveTabindex: Work in progress keyboard navigation
                        tabIndex={0}
                        data-selected={selectedCode === currency.code ? true : null}
                        className={C.ListItem}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCode(currency.code);
                        }}
                    >
                        <div className={C.item}>
                            <div className="flex items-center justify-between">
                                <div>{currency.title}</div>
                                <div className="w-60">
                                    {!currency.isSite ? <ExchangeRateExample currency={currency} /> : "-"}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </WModalContent>
    );
}

export default CurrencyButton;
